import React from 'react';
import { Button, Text } from 'grommet';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Box } from 'components/Wrapped';
import Loader from 'react-loader-spinner';

const StyledBox = styled(Box)`
  transition: background-color 0.3s;

  ${(props) =>
    props.theme === 'white' &&
    css`
      background: white;

      &:hover {
        background: #e5e5e5;
      }
    `};

  ${(props) =>
    props.theme === 'green' &&
    css`
      background: #568677;

      &:hover {
        background: #679285;
      }
    `};

  ${(props) =>
    props.theme === 'gray' &&
    css`
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(120px);

      &:hover {
        background: linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1)),
          linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1));
      }
    `};

  ${(props) =>
    props.theme === 'outline' &&
    css`
      background: transparent;
      backdrop-filter: blur(120px);
      border: 1px solid rgba(255, 255, 255, 0.16);

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.1);
      }
    `};

  ${(props) =>
    props.theme === 'black' &&
    css`
      background-color: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(120px);

      &:hover {
        background-color: #000000;
      }
    `};

  ${(props) =>
    props.theme === 'transparent' &&
    css`
      background-color: transparent;
      backdrop-filter: blur(120px);

      &:hover {
        background: transparent;
      }
    `};

  ${(props) =>
    props.theme === 'bright-green' &&
    css`
      background-color: #46b08e;

      &:hover {
        background: #56ba9a;
      }
    `};

  ${(props) =>
    props.theme === 'red' &&
    css`
      background-color: #c55445;

      &:hover {
        background: #bf5c4e;
      }
    `};
`;

const StyledButton = styled(Button)`
  display: flex;
  justify-content: center;

  &:focus {
    box-shadow: none;
  }

  ${(props) =>
    props.buttonSize === 'small' &&
    css`
      padding: 8px;
    `};

  ${(props) =>
    props.buttonSize === 'medium' &&
    css`
      padding: 11px 12px;
    `};

  ${(props) =>
    props.buttonSize === 'large' &&
    css`
      padding: 14px;
    `};

  ${(props) =>
    props.withoutPadding &&
    css`
      align-items: center;
      height: 100%;
      justify-content: center;
      padding: 0;
      width: 100%;
    `};
`;

const StyledText = styled(Text)`
  color: white;
  font-weight: 600;
  position: relative;

  ${(props) =>
    props.buttonSize === 'small' &&
    css`
      font-size: 12px;
      line-height: 8px;
    `};

  ${(props) =>
    props.buttonSize === 'medium' &&
    css`
      font-size: 14px;
      line-height: 18px;
    `};

  ${(props) =>
    props.buttonSize === 'large' &&
    css`
      font-size: 16px;
      line-height: 21px;
    `};

  ${(props) =>
    props.colorTheme === 'white' &&
    css`
      color: #00080c;
    `};
`;

const WrappedButtonNew = ({ primary, round, theme, label, buttonSize, height, width, margin, className, isLoading, ...rest }) =>
  isLoading ? (
    <Loader type={'Oval'} color={'var(--loadingColor)'} height={'25'} width={'25'} />
  ) : (
    <StyledBox pad="none" theme={theme} height={height} width={width} round={round} margin={margin} className={className}>
      <StyledButton
        buttonSize={buttonSize}
        primary
        withoutPadding={typeof label !== 'string' || isLoading}
        label={
          <StyledText colorTheme={theme} buttonSize={buttonSize}>
            {label}
          </StyledText>
        }
        {...rest}
      />
    </StyledBox>
  );

WrappedButtonNew.propTypes = {
  buttonSize: PropTypes.oneOf(['small', 'medium', 'large']),
  disabled: PropTypes.bool,
  theme: PropTypes.oneOf(['white', 'gray', 'green', 'outline', 'black', 'transparent', 'bright-green', 'red']),
  primary: PropTypes.bool,
  round: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  height: PropTypes.string,
  width: PropTypes.string,
  className: PropTypes.string,
};

WrappedButtonNew.defaultProps = {
  round: '100px',
  theme: 'white',
  buttonSize: 'medium',
  height: 'auto',
  width: 'auto',
};

export default WrappedButtonNew;
